import axios from "axios";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { toast } from "react-toastify";
import { parseRecipients } from "./recipients";

const MAX_TOAST_MESSAGE_LENGTH = 200;

function showSendSmsError(error) {
  const errorMessage =
    error.response?.data?.message || error.message || "Unable to send SMS.";
  console.error("Error Sending SMS.", errorMessage);
  const toastMessage =
    errorMessage.length > MAX_TOAST_MESSAGE_LENGTH
      ? `${errorMessage.slice(0, MAX_TOAST_MESSAGE_LENGTH)}...`
      : errorMessage;
  toast.error(toastMessage);
}

export function buildReportQueryString({ search, SD, ED }) {
  const parts = [];
  if (SD && ED) {
    parts.push(
      `startDate=${encodeURIComponent(SD)}`,
      `endDate=${encodeURIComponent(ED)}`
    );
  }
  if (search?.trim()) {
    parts.push(`search=${encodeURIComponent(search.trim())}`);
  }
  return parts.length ? `&${parts.join("&")}` : "";
}

export async function getWalletBalance({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_WALLETMODULE_BASEURL}/wallet/getWalletBalance?userId=${email}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting Wallet");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function fundWallet(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      amount: credentials.amount,
      method: credentials.method,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_WALLETMODULE_BASEURL}/payment/generatePaymentInfo`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Funding Wallet.");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function paymentGateway(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      amount: credentials.amount,
      method: credentials.method,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_WALLETMODULE_BASEURL}/payment/initiateNewPayment`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    toast.error(errorMessage);
    console.error('Error Initiating Paystack Payment:', error);
  }

  // catch (error) {
  //   console.error("Error Funding Paystack Wallet.");
  //   if (error.response) {
  //     console.error(error.response.data);
  //   }
  //   console.error(error);
  // }
}

export async function previewSMS(credentials) {
  var recipients;
  let payload;
  try {
    if (credentials.recipientType === "typeNumbers") {
      recipients = parseRecipients(credentials.typedNumbers).validNumbers;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "manual_input",
        recipients: recipients,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
      };
    } else if (credentials.recipientType === "upload") {
      recipients = credentials.file;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "file_upload",
        // fileUploaded: credentials.fileUploaded,
        uploadedFileName: credentials.uploadedFileName,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
      };
    } else if (credentials.recipientType === "contact_group") {
      recipients = credentials.file;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "contact_group",
        groupId: credentials.groupId,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
      };
    }
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/sms/preview/singleMessage`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    showSendSmsError(error);
  }
}

export async function sendSMS(credentials) {
  var recipients;
  let payload;
  try {
    if (credentials.recipientType === "typeNumbers") {
      recipients = parseRecipients(credentials.typedNumbers).validNumbers;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "manual_input",
        recipients: recipients,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
      };
    } else if (credentials.recipientType === "upload") {
      recipients = credentials.file;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "file_upload",
        // fileUploaded: credentials.fileUploaded,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
        uploadedFileName: credentials.uploadedFileName,
      };
    } else if (credentials.recipientType === "contact_group") {
      recipients = credentials.file;
      payload = {
        senderId: credentials.senderID,
        userId: credentials.userId,
        messageType: credentials.messageType,
        message: credentials.smsMessage,
        recipientInputMode: "contact_group",
        groupId: credentials.groupId,
        isScheduled: credentials.isScheduled,
        scheduleDateTime: credentials.scheduleDateTime,
      };
    }
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/sms/send/singleMessage`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    showSendSmsError(error);
  }
}

export async function previewSMSPersonalized(credentials) {
  let payload;
  try {
    payload = {
      senderId: credentials.sender_ID,
      userId: credentials.userId,
      messageType: credentials.messageType,
      messageTemplate: credentials.smsMessage,
      recipientInputMode: "file_upload",
      uploadedFileName: credentials.uploadedFileName,
      isScheduled: credentials.isScheduled,
      scheduleDateTime: credentials.scheduleDateTime,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/sms/preview/personalizedMessage`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Sending SMS.");
    if (error.response) {
      console.error(error.response.data.message);
      toast.error(error.response.data.message);
    }
    console.error(error);
  }
}

export async function sendSMSPersonalized(credentials) {
  let payload;
  try {
    payload = {
      senderId: credentials.sender_ID,
      userId: credentials.userId,
      messageType: credentials.messageType,
      messageTemplate: credentials.smsMessage,
      recipientInputMode: "file_upload",
      uploadedFileName: credentials.uploadedFileName,
      isScheduled: credentials.isScheduled,
      scheduleDateTime: credentials.scheduleDateTime,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/sms/send/personalizedMessage`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Sending SMS.");
    if (error.response) {
      console.error(error.response.data.message);
      toast.error(error.response.data.message);
    }
    console.error(error);
  }
}

export async function getAllSenderId({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/senderId/getAllSenderIds?userId=${email}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting SenderIdTR");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function getSMSUser({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/user/getUser?userId=${email}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting SMS User");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function sMsDay({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/totalSmsSentForTheDay?userId=${email}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting SMS Day Count");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function sMsMonth({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/totalSmsSentForTheMonth?userId=${email}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting SMS Month Count");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function getAllGroups({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/getAllGroups?userId=${email}`,
      { headers }
    );
    return {
      ok: true,
      data: callResp,
    };
  } catch (error) {
    console.error("Error Getting Contact Groups");
    if (error.response) {
      console.error(error.response.data.message);
      // toast.error(error.response.data.message);
      if (error.response.status === 404) {
        return {
          ok: false,
          error: "No contact group exist for this user",
        };
      } else {
        return {
          ok: false,
          error: "We ran into an issue, try again later",
        };
      }
    } else {
      return {
        ok: false,
        error: "We ran into an issue, try again later",
      };
    }
  }
}

export async function addSingleContact(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      groupId: credentials.groupId,
      phoneNumber: credentials.phoneNumber,
    };

    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/addSingleContact`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Sending SMS.");
    if (error.response) {
      console.error(error.response.data.message);
      toast.error(error.response.data.message);
    }
    console.error(error);
  }
}

export async function addMultipleContact(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      groupId: credentials.groupId,
      uploadedFileName: credentials.uploadedFileName,
    };
    // console.log(payload);
    // return;
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/addMultipleContacts`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Sending SMS.");
    if (error.response) {
      console.error(error.response.data.message);
      toast.error(error.response.data.message);
    }
    console.error(error);
  }
}

export async function createGroup(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      name: credentials.name,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.post(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/createGroup`,
      payload,
      { headers }
    );

    return callResp;
  } catch (error) {
    console.error("Error Creating Group.");
    if (error.response) {
      console.error(error.response.data.message);
      // toast.error(error.response.data.message);
    }
    console.error(error);
    throw error;
  }
}

export async function updateGroupName(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      groupId: credentials.groupId,
      name: credentials.name,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };
    const callResp = await axios.put(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/updateGroupName`,
      payload,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Creating Group.");
    if (error.response) {
      console.error(error.response.data.message);
      // toast.error(error.response.data.message);
    }
    console.error(error);
    throw error;
  }
}

export async function deleteGroupName(credentials) {
  try {
    const payload = {
      userId: credentials.userId,
      groupId: credentials.groupId,
    };
    const headers = { authorization: `Bearer ${credentials.bearerToken}` };

    const callResp = await axios.delete(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/deleteGroupName`,
      {
        headers,
        data: payload,
      }
    );

    return callResp;
  } catch (error) {
    console.error("Error Creating Group.");
    if (error.response) {
      console.error(error.response.data.message);
      // toast.error(error.response.data.message);
    }
    console.error(error);
    throw error;
  }
}

export async function getContacts(credentials) {
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage || 10,
      "x-pagination-page": credentials.page || 1,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    const download = credentials.download || false;
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/contactGroup/getContacts?userId=${credentials.userId}&groupId=${credentials.groupId}&download=${download}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting Contacts");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
    throw error;
  }
}

export async function smsTransactionDetailN11({ email, bearerToken }) {
  try {
    const headers = {
      "x-pagination-limit": "10",
      "x-pagination-page": "1",
      authorization: `Bearer ${bearerToken}`,
    };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/smsTransactionDetail?userId=${email}`,
      { headers }
    );
    // console.log("header", callResp.headers);
    // console.log("header1", callResp.headers["content-length"]);
    // console.log("header2", callResp.headers["x-pagination-totalpages"]);
    // console.log("header3", callResp.headers["x-pagination-totalcount"]);
    return callResp;
  } catch (error) {
    console.error("Error Getting Transaction Details");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function smsTransactionDetail(credentials) {
  const search = buildReportQueryString({
    search: credentials.search,
    SD: credentials.SD,
    ED: credentials.ED,
  });
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    // console.log("headers", headers);
    // console.log("search", search);
    // return;
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/smsTransactionDetail?userId=${credentials.email}${search}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting Transaction Details");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function smsTransactionDetailTenant(credentials) {
  let search;
  try {
    if (credentials.filteredItem) {
      search = `&${credentials.filteredItem}=${encodeURIComponent(credentials.searchedItem || "")}`;
    } else {
      search = buildReportQueryString({
        search: credentials.search,
        SD: credentials.SD,
        ED: credentials.ED,
      });
    }

    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    // console.log("headers", headers);
    // console.log("search", search);
    // return;
    const callResp = await axios.get(
      `${process.env.REACT_APP_RBACMODULE_BASEURL}/api/sms/search?userId=${credentials.email}${search}`,
      { headers }
    );

    if (!callResp.headers["x-pagination-totalcount"]) {
      callResp.headers["x-pagination-totalcount"] = 1;
    }
    if (!callResp.headers["x-pagination-totalpages"]) {
      callResp.headers["x-pagination-totalpages"] = 1;
    }
    return { headers: callResp.headers, data: callResp.data.data };
  } catch (error) {
    console.error("Error Getting Transaction Details");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function smsTransactions(credentials) {
  const search = buildReportQueryString({
    search: credentials.search,
    SD: credentials.SD,
    ED: credentials.ED,
  });
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    // console.log("search", search);
    // console.log("log", credentials);
    // return;
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/smsTransactions?userId=${credentials.email}${search}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting Transaction Report");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function scheduledJobs(credentials) {
  const search = buildReportQueryString({
    search: credentials.search,
    SD: credentials.SD,
    ED: credentials.ED,
  });
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/scheduledJobs?userId=${credentials.email}${search}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting scheduled Jobs Report");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function walletTransactions(credentials) {
  const search = buildReportQueryString({
    search: credentials.search,
    SD: credentials.SD,
    ED: credentials.ED,
  });
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    const callResp = await axios.get(
      `${process.env.REACT_APP_WALLETMODULE_BASEURL}/reports/walletTransactions?userId=${credentials.email}${search}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting scheduled Jobs Report");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function paymentRequests(credentials) {
  const search = buildReportQueryString({
    search: credentials.search,
    SD: credentials.SD,
    ED: credentials.ED,
  });
  try {
    const headers = {
      "x-pagination-limit": credentials.perPage,
      "x-pagination-page": credentials.page,
      authorization: `Bearer ${credentials.bearerToken}`,
    };
    const callResp = await axios.get(
      `${process.env.REACT_APP_WALLETMODULE_BASEURL}/reports/paymentRequests?userId=${credentials.email}${search}`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting scheduled Jobs Report");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export async function allList({ email, bearerToken }) {
  const getGrp = await getAllGroups({
    email,
    bearerToken,
  });
  if (getGrp.ok) {
    // console.log("if ok", getGrp.data);
    return getGrp.data;
  } else {
    // console.log(getGrp.error);
    return getGrp.error;
  }
}

export async function getAllNetworks({ email, bearerToken }) {
  try {
    const headers = { authorization: `Bearer ${bearerToken}` };
    const callResp = await axios.get(
      `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/getAllNetworks`,
      { headers }
    );
    return callResp;
  } catch (error) {
    console.error("Error Getting getAllNetworks");
    if (error.response) {
      console.error(error.response.data);
    }
    console.error(error);
  }
}

export const MAX_SMS_EXPORT_DATE_RANGE_DAYS = 90;

export async function startSmsTransactionDetailExport({
  email,
  bearerToken,
  startDate,
  endDate,
  search,
}) {
  const headers = { authorization: `Bearer ${bearerToken}` };
  const body = {
    userId: email,
    startDate,
    endDate,
  };
  if (search?.trim()) {
    body.search = search.trim();
  }

  const callResp = await axios.post(
    `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/smsTransactionDetail/export`,
    body,
    { headers }
  );
  return callResp;
}

export async function subscribeSmsExportEvents({
  jobId,
  bearerToken,
  userId,
  onEvent,
  onError,
  signal,
}) {
  const url = `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/exports/${jobId}/events?userId=${encodeURIComponent(userId)}`;

  await fetchEventSource(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    signal,
    onmessage(message) {
      if (!message.data) {
        return;
      }
      try {
        const payload = JSON.parse(message.data);
        onEvent(message.event || "message", payload);
      } catch (error) {
        console.error("Failed to parse SSE message", error);
      }
    },
    onerror(error) {
      if (signal?.aborted) {
        return;
      }
      if (onError) {
        onError(error);
      }
    },
  });
}

export async function downloadSmsExportFile({
  jobId,
  bearerToken,
  userId,
  fileName,
}) {
  const headers = { authorization: `Bearer ${bearerToken}` };
  const callResp = await axios.get(
    `${process.env.REACT_APP_SMSMODULE_BASEURL}/reports/exports/${jobId}/download?userId=${encodeURIComponent(userId)}`,
    { headers, responseType: "blob" }
  );

  const blobUrl = window.URL.createObjectURL(new Blob([callResp.data]));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName || "sms-transaction-details.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}
